import type { UnitType } from "./unit.js";
import type { DataSchema, ValidatorType } from "shelving";
import {
	ALLOW_STRING,
	AllowStringSchema,
	BOOLEAN,
	BooleanSchema,
	DATA,
	DATE,
	DICTIONARY,
	DictionarySchema,
	Feedback,
	Feedbacks,
	INTEGER,
	KEY,
	KeySchema,
	NBSP,
	NumberSchema,
	OPTIONAL,
	OPTIONAL_INTEGER,
	OPTIONAL_KEY,
	OPTIONAL_TIMESTAMP,
	OptionalSchema,
	StringSchema,
	TIMESTAMP,
	formatNumber,
	getValidationContext,
	isFuture,
	isPast,
	pickProps,
} from "shelving";
import { formatCompactNumber } from "shared/util/number";
import { KEYWORD_FIELDS } from "../util/keyword.js";
import { PRIVACY } from "../util/privacy.js";
import { CREATOR_ENTITY, ENTITIES } from "./entity.js";
import { ABOUT, NAME } from "./misc.js";
import { UNIT_TYPES, convertUnitAmount, formatUnitAmount, formatUnitAmountAs, getMainUnit } from "./unit.js";

// Constants.
export const PROGRESS_SEPARATOR = `${NBSP}/${NBSP}`;
export const CHALLENGE_MAX = 48;
export const ATTEMPT_MAX = 48;
export const TASK_POINTS = 10; // Points awarded for making progress on a task.
export const CHALLENGE_POINTS = 100; // Points awarded for completing a challenge.
export const TASKS_LIMIT = 26; // Maximum number of tasks on a challenge (26 because internal task IDs use `a` to `z`).
export const MIN_SHARED_FACTOR = 2; // Shared target must be at least 2x individual target.

// Schemas.
const AMOUNT_SCHEMA = new NumberSchema({ title: "Amount" });
const UNITS_SCHEMA = new KeySchema({ title: "Units" });
const OPTIONAL_UNITS_SCHEMA = OPTIONAL(UNITS_SCHEMA);

/** Types of challenge task. */
export type TaskType = UnitType | "checkbox" | "number" | "counter";

/** List of types of challenge task. */
export const TASK_TYPES: {
	[K in TaskType]: { readonly title: string };
} = {
	checkbox: { title: "Checkbox" },
	number: { title: "Number" },
	counter: { title: "Counter" },
	...UNIT_TYPES,
};

/** A task type string like `checkbox` or `distance` */
export const TASK_TYPE = new AllowStringSchema({ allow: TASK_TYPES, title: "Type" });

/** Schema for a task in a challenge (has additional validation to ensure `unit` is valid for `type`). */
const _TASK_SCHEMA = DATA({
	// Details.
	title: new StringSchema({ title: "Title", placeholder: "e.g. Run 10 km five times", min: 1, max: 25 }),
	type: TASK_TYPE,
	units: OPTIONAL_KEY,
	soloTarget: new NumberSchema({ min: 1, value: 1, title: "Individual target", placeholder: "e.g. 250", step: 0.01 }),
	shared: new BooleanSchema({ title: "Collaborative", value: false }),
	sharedTarget: new NumberSchema({ min: 1, value: 1, title: "Shared target", placeholder: "e.g. 250,000", step: 0.01 }),
	// Counts.
	total: INTEGER, // Total progress across all users.
});
export const TASK_SCHEMA: DataSchema<TaskData> = {
	..._TASK_SCHEMA,
	validate(unsafeValue: unknown): TaskData {
		const task = _TASK_SCHEMA.validate(unsafeValue);
		const { action } = getValidationContext();
		if (action !== "get") {
			const { type, units, shared, soloTarget, sharedTarget = 0 } = task;
			if (type) {
				// If type is number or counter or checkbox: units is null.
				// If type is a unit type: units is a valid unit for that type or the default unit for that type.
				if (type === "number" || type === "counter" || type === "checkbox") task.units = null;
				else task.units = getMainUnit(type, units).key;
			}
			if (soloTarget && shared) {
				// If the task is collaborative: shared target must be more than than target.
				// If the task is non-collaborative: shared target is the same as target.
				if (sharedTarget < soloTarget * MIN_SHARED_FACTOR) throw new Feedbacks({ sharedTarget: new Feedback(`Must be at least ${formatNumber(MIN_SHARED_FACTOR * soloTarget)}`) });
			}
		}
		return task;
	},
};
export type TaskData = ValidatorType<typeof _TASK_SCHEMA>;

/** Format a task as a string, e.g. `Accumulate running distance (2500 km)` */
export const formatTask = ({ title, soloTarget: target, type, units }: TaskData): string => `${title} (${formatTaskAmount(target, type, units)})`;

/** Schema for a challenge.  */
export const CHALLENGE_SCHEMA = DATA({
	creator: CREATOR_ENTITY,
	verified: BOOLEAN,
	slug: KEY, // Path segment shown in the URL based on the title, e.g. `gold-challenge` (created with `toSlug()`).
	// Details.
	privacy: PRIVACY,
	name: NAME,
	about: ABOUT,
	tags: ENTITIES,
	...KEYWORD_FIELDS,
	// Tasks.
	tasks: new DictionarySchema({
		title: "Tasks",
		items: TASK_SCHEMA,
		min: 1,
		max: TASKS_LIMIT,
	}),
	// Rewards.
	badge: new KeySchema({ title: "Badge" }),
	background: new KeySchema({ title: "Background" }),
	reaction: new OptionalSchema({ title: "Reaction", source: KEY }),
	// Limits.
	start: new OptionalSchema({ title: "Start date", source: DATE }),
	end: new OptionalSchema({ title: "End date", source: DATE }),
	// Counts.
	started: INTEGER, // Number of times the challenge has been started (globally).
	completed: INTEGER, // Number of times the challenge has been completed (globally).
	reactions: INTEGER,
	// Dates.
	created: TIMESTAMP,
	updated: TIMESTAMP,
});
export type ChallengeData = ValidatorType<typeof CHALLENGE_SCHEMA>;

// Subsets of challenge schema.
export const CHALLENGE_DETAILS_SCHEMA = DATA(pickProps(CHALLENGE_SCHEMA.props, "name", "about", "privacy", "start", "end"));
export const CHALLENGE_REWARDS_SCHEMA = DATA(pickProps(CHALLENGE_SCHEMA.props, "badge", "background", "reaction"));
export const CHALLENGE_FORM_SCHEMA = DATA({
	...CHALLENGE_DETAILS_SCHEMA.props,
	...CHALLENGE_REWARDS_SCHEMA.props,
	...pickProps(CHALLENGE_SCHEMA.props, "creator", "tasks"),
});
export type ChallengeFormData = ValidatorType<typeof CHALLENGE_FORM_SCHEMA>;

/** Status of an attempt of a challenge. */
export const ATTEMPT_STATUSES = {
	active: "Active",
	completed: "Completed",
};
export type AttemptStatus = keyof typeof ATTEMPT_STATUSES;

/** Progress made on an entire challenge. */
export const ATTEMPT_SCHEMA = DATA({
	uid: KEY, // UID of the user who is attempting the challenge.
	cid: KEY, // Challenge ID of the challenge that's being attempted.
	status: ALLOW_STRING(ATTEMPT_STATUSES),
	started: TIMESTAMP,
	updated: OPTIONAL_TIMESTAMP,
	completed: OPTIONAL_INTEGER,
	totals: DICTIONARY(INTEGER), // Always converted to task's units so it can be compared to `task.tasks.${tid}.target`
	badge: CHALLENGE_SCHEMA.props.badge,
	background: CHALLENGE_SCHEMA.props.background,
	reaction: CHALLENGE_SCHEMA.props.reaction,
	privacy: PRIVACY,
});
export type AttemptData = ValidatorType<typeof ATTEMPT_SCHEMA>;

/** Get the item for an attempt. */
export const getAttemptID = (cid: string, uid: string) => `${uid}:${cid}`;

/** Progress on a task at a point in time. */
export const PROGRESS_SCHEMA = DATA({
	uid: KEY,
	cid: KEY,
	tid: KEY,
	date: INTEGER,
	type: TASK_TYPE,
	userAmount: AMOUNT_SCHEMA,
	userUnits: OPTIONAL_UNITS_SCHEMA, // Units the user entered the amount in.
	amount: AMOUNT_SCHEMA,
	units: OPTIONAL_UNITS_SCHEMA, // Units the task was created in.
});
export type ProgressData = ValidatorType<typeof PROGRESS_SCHEMA>;

/** Is a challenge before its start date? */
export function isChallengeEarly(challenge: ChallengeData): challenge is ChallengeData & { start: number } {
	const { start } = challenge;
	return start !== null && isFuture(start);
}

/** Has a challenge passed its end date? */
export function isChallengeEnded(challenge: ChallengeData): challenge is ChallengeData & { end: number } {
	const { end } = challenge;

	return end !== null && isPast(end);
}

/** Is a challenge inside its start/end dates. */
export const isChallengeStartable = (challenge: ChallengeData): boolean => !isChallengeEnded(challenge) && !isChallengeEarly(challenge);

/** Is a challenge done based on the progress made on its tasks? */
export function isChallengeDone(tasks: ChallengeData["tasks"], totals: AttemptData["totals"]): boolean {
	for (const [id, task] of Object.entries(tasks)) {
		const { [id]: progress = 0 } = totals;
		if (!isTaskDone(task, progress)) return false;
	}
	return true;
}

/** Is a task done based on its total progress? */
export function isTaskDone({ shared, soloTarget: target, total: sharedTotal }: TaskData, soloTotal: number): boolean {
	// If it's a collaborative task use the shared progress for everyone attempting this challenge.
	// Otherwise use the user's solo progress.
	return (shared ? sharedTotal : soloTotal) >= target;
}

/**
 * Convert an amount from a source unit to a target unit
 * - `null` units are assumed to be default unit for unit type.
 */
export function convertTaskAmount(amount: number, type: TaskType, sourceUnits: string | null, targetUnits: string | null): number {
	if (type === "number" || type === "counter" || type === "checkbox") return amount;
	return convertUnitAmount(amount, type, sourceUnits as string, targetUnits as string);
}

export function formatTaskAmount(amount: number, type: TaskType, units: string | null): string {
	if (type === "number") return formatCompactNumber(amount);
	if (type === "counter") return formatCompactNumber(amount);
	if (type === "checkbox") return amount ? "Yes" : "No";
	return formatUnitAmount(amount, type, units as string);
}

export function formatTaskAmountAs(amount: number, type: TaskType, sourceUnits: string | null, targetUnits: string | null): string {
	if (type === "number") return formatCompactNumber(amount);
	if (type === "counter") return formatCompactNumber(amount);
	if (type === "checkbox") return amount ? "Yes" : "No";
	return formatUnitAmountAs(amount, type, sourceUnits as string, targetUnits as string);
}

/** Format completion for a task, e.g. `12 / 14.3M km` */
export function formatTaskCompletion(progress: number, total: number, type: TaskType, units: string | null): string {
	if (type === "checkbox") return progress >= total ? "Done" : "";
	return `${formatCompactNumber(progress)}${PROGRESS_SEPARATOR}${formatTaskAmount(total, type, units)}`;
}

export const EMPTY_TASK: TaskData = {
	title: "First task...",
	type: "checkbox",
	units: null,
	soloTarget: 1,
	sharedTarget: 1,
	total: 0,
	shared: false,
};

export const EMPTY_CHALLENGE = {
	name: "My Challenge",
	about: "",
	tasks: { a: EMPTY_TASK },
	badge: "none",
	background: "none",
	reaction: null,
	start: null,
	end: null,
	privacy: "private",
} satisfies Partial<ChallengeData>;

/** Combination of an amount and a set of units is a _quantity_. */
export const QUANTITY_SCHEMA = DATA({
	amount: AMOUNT_SCHEMA,
	units: OPTIONAL_UNITS_SCHEMA,
});
export type QuantityData = ValidatorType<typeof QUANTITY_SCHEMA>;
